import type { Program } from '@shared/types.ts';

/**
 * Turning a guide into a grid.
 *
 * Pure functions, deliberately: this is the arithmetic a cable box gets right
 * without thinking about, and it is the part most worth testing without a
 * browser. Everything is milliseconds in, pixels out.
 */

/** How wide an hour is. The single knob that sets the guide's whole feel. */
export const PX_PER_HOUR = 320;
export const PX_PER_MS = PX_PER_HOUR / 3_600_000;

/** The channel column, which never scrolls horizontally. */
export const CHANNEL_COL_PX = 200;
export const ROW_HEIGHT_PX = 64;

export interface Block {
  program: Program;
  startMs: number;
  endMs: number;
  /** Pixels from the left edge of the timeline. */
  left: number;
  width: number;
}

export function toBlocks(programs: Program[], originMs: number): Block[] {
  const out: Block[] = [];
  for (const p of programs) {
    const startMs = Date.parse(p.startUtc);
    const endMs = Date.parse(p.endUtc);
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) continue;
    out.push({
      program: p,
      startMs,
      endMs,
      left: (startMs - originMs) * PX_PER_MS,
      width: (endMs - startMs) * PX_PER_MS,
    });
  }
  return out;
}

/**
 * The programme covering an instant, or the nearest one after it.
 *
 * Used when moving up or down a row: a cable guide keeps your place in TIME,
 * not your place in a list, so changing channel lands you on whatever is on at
 * the same moment rather than on the Nth programme of the new row.
 */
export function blockAt(blocks: Block[], atMs: number): number {
  if (blocks.length === 0) return -1;
  for (let i = 0; i < blocks.length; i++) {
    if (atMs < blocks[i].endMs) return i;
  }
  return blocks.length - 1;
}

/** Half-hour ticks across the window, for the time header. */
export function ticks(originMs: number, spanMs: number, everyMs = 30 * 60_000): number[] {
  const first = Math.ceil(originMs / everyMs) * everyMs;
  const out: number[] = [];
  for (let t = first; t < originMs + spanMs; t += everyMs) out.push(t);
  return out;
}

/**
 * Keep a range visible inside a viewport by moving the viewport as little as
 * possible — the behaviour that makes a guide feel like it is following you
 * rather than jumping around.
 */
export function scrollToShow(
  offset: number, viewport: number, start: number, end: number, pad = 0,
): number {
  if (start - pad < offset) return Math.max(0, start - pad);
  if (end + pad > offset + viewport) return Math.max(0, end + pad - viewport);
  return offset;
}

export function clockLabel(ms: number): string {
  return new Date(ms).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

export function slotLabel(startMs: number, endMs: number): string {
  return `${clockLabel(startMs)} – ${clockLabel(endMs)}`;
}
